<template>
  <AdminLayout breadcrumb="Dashboard" @logout="logout">
    <div class="space-y-6">
      <!-- Welcome Section -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 mb-2">
              Welkom terug, {{ getUserName() }}!
            </h1>
            <p class="text-gray-600">
              Jouw Selectair Intranet Dashboard
            </p>
          </div>
          <div class="text-right">
            <p class="text-sm text-gray-500">{{ getCurrentDate() }}</p>
            <div class="mt-2 w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center">
              <span class="text-sm font-semibold text-white">{{ userInitials }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Navigation -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <!-- Home -->
        <router-link to="/admin/home" class="group">
          <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 group-hover:border-blue-200">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 group-hover:text-blue-600">Home</h3>
                <p class="text-sm text-gray-500">Hoofdpagina</p>
              </div>
            </div>
          </div>
        </router-link>

        <!-- Informatie -->
        <div class="group cursor-pointer" @click="showInformatieMenu = !showInformatieMenu">
          <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 group-hover:border-green-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900 group-hover:text-green-600">Informatie</h3>
                  <p class="text-sm text-gray-500">6 onderdelen</p>
                </div>
              </div>
              <svg class="h-8 w-8 text-gray-400 transition-transform" :class="{ 'rotate-180': showInformatieMenu }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
            
            <!-- Submenu -->
            <div v-if="showInformatieMenu" class="mt-4 pt-4 border-t space-y-2">
              <router-link to="/admin/agentmails" class="block text-sm text-gray-600 hover:text-green-600 py-1">AgentMails</router-link>
              <router-link to="/admin/agenda" class="block text-sm text-gray-600 hover:text-green-600 py-1">Agenda</router-link>
              <router-link to="/admin/tui-statistieken" class="block text-sm text-gray-600 hover:text-green-600 py-1">TUI Statistieken</router-link>
              <router-link to="/admin/statuten-en-reglementen" class="block text-sm text-gray-600 hover:text-green-600 py-1">Statuten en reglementen</router-link>
              <router-link to="/admin/ledenvoordelen" class="block text-sm text-gray-600 hover:text-green-600 py-1">Ledenvoordelen</router-link>
              <router-link to="/admin/wie-is-wie" class="block text-sm text-gray-600 hover:text-green-600 py-1">Wie is wie</router-link>
            </div>
          </div>
        </div>

        <!-- Partners -->
        <router-link to="/admin/partners" class="group">
          <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 group-hover:border-purple-200">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 group-hover:text-purple-600">Partners</h3>
                <p class="text-sm text-gray-500">Reispartners</p>
              </div>
            </div>
          </div>
        </router-link>

        <!-- Marketing -->
        <div class="group cursor-pointer" @click="showMarketingMenu = !showMarketingMenu">
          <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 group-hover:border-red-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900 group-hover:text-red-600">Marketing</h3>
                  <p class="text-sm text-gray-500">7 tools</p>
                </div>
              </div>
              <svg class="h-8 w-8 text-gray-400 transition-transform" :class="{ 'rotate-180': showMarketingMenu }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
            
            <!-- Submenu -->
            <div v-if="showMarketingMenu" class="mt-4 pt-4 border-t space-y-2">
              <router-link to="/admin/materiaal-bestellen-via-staci" class="block text-sm text-gray-600 hover:text-red-600 py-1">Materiaal bestellen</router-link>
              <router-link to="/admin/beeldmateriaal" class="block text-sm text-gray-600 hover:text-red-600 py-1">Beeldmateriaal</router-link>
              <router-link to="/admin/tutorials?category=Nieuwsbrief" class="block text-sm text-gray-600 hover:text-red-600 py-1">Nieuwsbrief</router-link>
              <router-link to="/admin/cadeaubonnen" class="block text-sm text-gray-600 hover:text-red-600 py-1">Cadeaubonnen</router-link>
              <router-link to="/admin/postkaartjes" class="block text-sm text-gray-600 hover:text-red-600 py-1">Postkaartjes</router-link>
              <router-link to="/admin/webinars" class="block text-sm text-gray-600 hover:text-red-600 py-1">Webinars</router-link>
              <router-link to="/admin/brochures-bestellen" class="block text-sm text-gray-600 hover:text-red-600 py-1">Brochures bestellen</router-link>
            </div>
          </div>
        </div>

        <!-- Handleidingen -->
        <router-link to="/admin/handleidingen" class="group">
          <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 group-hover:border-orange-200">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 group-hover:text-orange-600">Handleidingen</h3>
                <p class="text-sm text-gray-500">Tutorials</p>
              </div>
            </div>
          </div>
        </router-link>

        <!-- Incentives -->
        <router-link to="/admin/incentives" class="group">
          <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 group-hover:border-yellow-200">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 group-hover:text-yellow-600">Incentives</h3>
                <p class="text-sm text-gray-500">Beloningen</p>
              </div>
            </div>
          </div>
        </router-link>

        <!-- Wetgeving -->
        <router-link to="/admin/wetgeving" class="group">
          <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 group-hover:border-gray-300">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 group-hover:text-gray-600">Wetgeving</h3>
                <p class="text-sm text-gray-500">Regelgeving</p>
              </div>
            </div>
          </div>
        </router-link>

        <!-- Folders -->
        <router-link to="/admin/folders" class="group">
          <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 group-hover:border-indigo-200">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 group-hover:text-indigo-600">Folders</h3>
                <p class="text-sm text-gray-500">Bestanden</p>
              </div>
            </div>
          </div>
        </router-link>
      </div>

      <!-- News Section -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-gray-900">Laatste Nieuws</h2>
          <div v-if="loadingFeed" class="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        </div>
        
        <div v-if="rssItems.length > 0" class="space-y-4">
          <div 
            v-for="item in rssItems.slice(0, 3)" 
            :key="item.title" 
            class="border-l-4 border-red-500 pl-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors rounded-r-lg"
            @click="openInSidebar(item.link, item.title)"
          >
            <h3 class="font-medium text-gray-900 text-sm hover:text-red-600 transition-colors">{{ item.title }}</h3>
            <p class="text-xs text-gray-500 mt-1">{{ formatDate(item.pubDate) }}</p>
            <p v-if="item.description" class="text-sm text-gray-600 mt-1 line-clamp-2">{{ item.description }}</p>
            <p class="text-xs text-blue-600 mt-1 truncate hover:underline">{{ item.link }}</p>
          </div>
        </div>
        
        <div v-else-if="!loadingFeed" class="text-center py-4">
          <p class="text-sm text-gray-500">Geen nieuws beschikbaar</p>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Snelle Acties</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <router-link to="/admin/tutorials" class="flex items-center space-x-2 p-3 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors group">
            <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-7-9h6a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2V7a2 2 0 012-2z"></path>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-blue-600">Tutorials</span>
          </router-link>

          <router-link to="/admin/tutorials?category=Nieuwsbrief" class="flex items-center space-x-2 p-3 rounded-lg bg-gray-50 hover:bg-red-50 transition-colors group">
            <div class="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-red-600">Nieuwsbrief</span>
          </router-link>

          <router-link to="/admin/folders" class="flex items-center space-x-2 p-3 rounded-lg bg-gray-50 hover:bg-green-50 transition-colors group">
            <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-green-600">Nieuwe Folder</span>
          </router-link>

          <router-link to="/admin/partners" class="flex items-center space-x-2 p-3 rounded-lg bg-gray-50 hover:bg-purple-50 transition-colors group">
            <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg class="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-purple-600">Partners</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Sidebar Overlay -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 overflow-hidden"
      style="z-index: 9999;"
      @click="closeSidebar"
    >
      <!-- Background Overlay -->
      <div
        class="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
      ></div>

      <!-- Sidebar -->
      <div
        class="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform sm:max-w-2xl md:max-w-3xl"
        :class="{
          'translate-x-0': sidebarOpen,
          'translate-x-full': !sidebarOpen,
        }"
        @click.stop
      >
        <!-- Sidebar Header -->
        <div
          class="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50"
        >
          <div class="flex-1 min-w-0">
            <h2 class="text-lg font-semibold text-gray-900 truncate">
              {{ sidebarTitle }}
            </h2>
            <p class="text-sm text-gray-500 truncate mt-1">
              {{ sidebarUrl }}
            </p>
          </div>
          <button
            @click="closeSidebar"
            class="ml-4 flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <!-- Sidebar Content -->
        <div class="flex-1 overflow-hidden" style="height: calc(100vh - 150px)">
          <!-- Loading State -->
          <div
            v-if="sidebarLoading"
            class="flex justify-center items-center h-64"
          >
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"
            ></div>
          </div>

          <!-- Error State -->
          <div v-else-if="sidebarError" class="p-4">
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
              <div class="flex items-center">
                <svg
                  class="w-5 h-5 text-red-600 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.064 16.5c-.77.833.192 2.5 1.732 2.5z"
                  ></path>
                </svg>
                <div>
                  <h3 class="text-red-800 font-medium">
                    Error Loading Content
                  </h3>
                  <p class="text-red-700 text-sm mt-1">{{ sidebarError }}</p>
                </div>
              </div>
              <div class="mt-4 flex space-x-3">
                <button
                  @click="loadSidebarContent"
                  class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                >
                  Retry
                </button>
                <a
                  :href="sidebarUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                >
                  Open in New Tab
                </a>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div v-else class="h-full">
            <iframe
              v-if="sidebarContent && isValidUrl(sidebarUrl)"
              :src="sidebarUrl"
              class="w-full h-full border-0 sidebar-iframe"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-top-navigation"
              referrerpolicy="no-referrer-when-downgrade"
              loading="lazy"
            ></iframe>
            <div v-else-if="sidebarContent" class="p-4 overflow-auto h-full">
              <div v-html="sidebarContent" class="prose max-w-none"></div>
            </div>
            <!-- Fallback message for blocked content -->
            <div
              v-else-if="sidebarError && sidebarError.includes('blocked')"
              class="p-4 text-center"
            >
              <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div class="flex items-center justify-center mb-4">
                  <svg
                    class="w-12 h-12 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.064 16.5c-.77.833.192 2.5 1.732 2.5z"
                    ></path>
                  </svg>
                </div>
                <h3 class="text-lg font-semibold text-yellow-800 mb-2">
                  Content Blocked
                </h3>
                <p class="text-yellow-700 mb-4">
                  This content has been blocked by your browser's security
                  settings (likely Brave's ad blocker or privacy shields).
                </p>
                <div class="space-y-2">
                  <p class="text-sm text-yellow-600">
                    <strong>To view this content:</strong>
                  </p>
                  <ul class="text-sm text-yellow-600 text-left space-y-1">
                    <li>• Disable Brave Shields for this site</li>
                    <li>• Allow all scripts and frames</li>
                    <li>• Or click "Open in New Tab" below</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar Footer -->
        <div class="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div class="flex items-center justify-between">
            <div class="flex space-x-3">
              <a
                :href="sidebarUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  ></path>
                </svg>
                Open in New Tab
              </a>
              <button
                @click="copySidebarUrl"
                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                  ></path>
                </svg>
                Copy URL
              </button>
            </div>
            <button
              @click="closeSidebar"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/components/AdminLayout.vue";

export default {
  name: "AdminDashboard",
  components: {
    AdminLayout,
  },
  data() {
    return {
      userEmail: "",
      showInformatieMenu: false,
      showMarketingMenu: false,
      rssItems: [],
      loadingFeed: false,
      // Sidebar state
      sidebarOpen: false,
      sidebarLoading: false,
      sidebarError: null,
      sidebarContent: "",
      sidebarUrl: "",
      sidebarTitle: "",
    };
  },
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {};
    },
    apiUrl() {
      return this.shopSettings?.apiUrl || "https://selectair-vakanties.uptodatesites.com";
    },
    userInitials() {
      if (!this.userEmail) return "U";
      const parts = this.userEmail.split("@")[0].split(".");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return this.userEmail[0].toUpperCase();
    },
  },
  mounted() {
    // Get user email from localStorage
    try {
      const user = localStorage.getItem("admin_user");
      if (user) {
        const userData = JSON.parse(user);
        this.userEmail = userData.email;
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
    
    // Fetch RSS feed
    this.fetchRssFeed();

    // Add keydown listener for ESC key
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeUnmount() {
    // Remove keydown listener
    window.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    logout() {
      // Clear auth data
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      localStorage.removeItem("uptodate_token");
      localStorage.removeItem("currentClient");
      // Redirect to login
      this.$router.push("/admin/login");
    },
    getUserName() {
      if (!this.userEmail) return "Gebruiker";
      const name = this.userEmail.split("@")[0];
      // Capitalize first letter and replace dots with spaces
      return name
        .split(".")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    getCurrentDate() {
      const today = new Date();
      return today.toLocaleDateString("nl-NL", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
    },
    async fetchRssFeed() {
      try {
        this.loadingFeed = true;
        const axios = await import('axios');
        const response = await axios.default.get(`${this.apiUrl}/api/admin/rss-feed`);
        
        if (response.data.success) {
          this.rssItems = response.data.data.items;
        }
      } catch (error) {
        console.error('Error fetching RSS feed:', error);
        // Set some sample data as fallback
        // this.rssItems = [
        //   { 
        //     title: 'Selectair Admin System Update', 
        //     pubDate: new Date().toISOString(), 
        //     description: 'Nieuwe bestandsbeheer functies beschikbaar in het admin panel.',
        //   },
        //   { 
        //     title: 'Beveiligingsverbetering', 
        //     pubDate: new Date(Date.now() - 86400000).toISOString(), 
        //     description: 'Verbeterde authenticatie en toegangscontroles geïmplementeerd.',
        //   },
        //   { 
        //     title: 'Prestatie Verbeteringen', 
        //     pubDate: new Date(Date.now() - 172800000).toISOString(), 
        //     description: 'Snellere bestandsuploads en downloads nu beschikbaar.',
        //   }
        // ];
      } finally {
        this.loadingFeed = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    openInSidebar(url, title = "") {
      console.log("Opening in sidebar:", url);

      this.sidebarUrl = url;
      this.sidebarTitle = title || this.getUrlTitle(url);
      this.sidebarOpen = true;
      this.sidebarError = null;
      this.sidebarContent = null;

      // Load content
      this.loadSidebarContent();
    },
    closeSidebar() {
      this.sidebarOpen = false;
      this.sidebarContent = "";
      this.sidebarError = null;
      this.sidebarLoading = false;
    },
    async loadSidebarContent() {
      this.sidebarLoading = true;
      this.sidebarError = null;

      try {
        // Check if it's a valid URL for iframe
        if (this.isValidUrl(this.sidebarUrl)) {
          // For external URLs, we'll use iframe with fallback detection
          this.sidebarContent = "iframe";

          // Add iframe load detection
          this.$nextTick(() => {
            const iframe = document.querySelector(".sidebar-iframe");
            if (iframe) {
              iframe.onload = () => {
                console.log("Iframe loaded successfully");
              };

              iframe.onerror = () => {
                console.log("Iframe failed to load");
                this.sidebarError =
                  "Content blocked by browser security settings";
              };

              // Detect if iframe is blocked (common in Brave/Firefox)
              setTimeout(() => {
                try {
                  const iframeDoc =
                    iframe.contentDocument || iframe.contentWindow.document;
                  if (!iframeDoc) {
                    this.sidebarError =
                      "Content blocked by browser security settings";
                  }
                } catch (e) {
                  // This is expected for cross-origin iframes
                  console.log("Cross-origin iframe detected (normal)");
                }
              }, 1000);
            }
          });
        } else {
          // For internal or problematic URLs, try to fetch content
          const response = await fetch(this.sidebarUrl, {
            mode: "cors",
            headers: {
              Accept:
                "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const content = await response.text();
          this.sidebarContent = content;
        }
      } catch (error) {
        console.error("Error loading sidebar content:", error);
        if (
          error.message.includes("blocked") ||
          error.message.includes("CORS")
        ) {
          this.sidebarError = "Content blocked by browser security settings";
        } else {
          this.sidebarError = error.message || "Failed to load content";
        }
      } finally {
        this.sidebarLoading = false;
      }
    },
    isValidUrl(url) {
      try {
        const urlObj = new URL(url);
        return ["http:", "https:"].includes(urlObj.protocol);
      } catch {
        return false;
      }
    },
    getUrlTitle(url) {
      try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.replace("www.", "");
        const pathname = urlObj.pathname;

        if (pathname && pathname !== "/") {
          const lastSegment = pathname.split("/").pop();
          if (lastSegment) {
            return lastSegment.replace(/[-_]/g, " ").replace(/\.[^/.]+$/, "");
          }
        }

        return hostname;
      } catch {
        return url;
      }
    },
    async copySidebarUrl() {
      try {
        await navigator.clipboard.writeText(this.sidebarUrl);
        console.log("URL copied to clipboard");
        // You could add a toast notification here
      } catch (error) {
        console.error("Failed to copy URL:", error);
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = this.sidebarUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
    },
    handleKeydown(event) {
      // Close sidebar on ESC key
      if (event.key === "Escape" && this.sidebarOpen) {
        this.closeSidebar();
      }
    },
  },
};
</script>

<style scoped>
/* Simple hover effects */
.group:hover .group-hover\:scale-110 {
  transform: scale(1.1);
}

/* Line clamp utility */
.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>